import COVER_IMAGE from "../assets/login_image.jpg";
import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";

const LoginPage = ({ setIsAuthenticated }) => {
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    rememberMe: false
  });
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});
  const navigate = useNavigate();

  // Retrieves remembered email on component mount
  useEffect(() => {
    const rememberedEmail = localStorage.getItem("rememberedEmail");
    if (rememberedEmail) {
      setFormData(prev => ({ ...prev, email: rememberedEmail }));
    }
  }, []);

  // Validates form fields
  const validateForm = () => {
    // Future validation code - uncomment when we have a real email as login to db
    /*
    const errors = {};
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (!formData.email) {
      errors.email = "Email is required";
    } else if (!emailRegex.test(formData.email)) {
      errors.email = "Please enter a valid email";
    }

    if (!formData.password) {
      errors.password = "Password is required";
    }

    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
    */
    return true; // Always validates for now
  };

  // Handles the login process
  const handleLogin = async (e) => {
    e.preventDefault();
    setError(null);
    localStorage.removeItem("previousPage");
    localStorage.removeItem("sortConfig")

    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      const response = await fetch("/api/login", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          email: formData.email,
          password: formData.password
        }),
      });

      const data = await response.text();

      if (!response.ok) {
        throw new Error(data || "Login failed. Please check your credentials.");
      }

      const { token } = JSON.parse(data);

      if (!token) {
        throw new Error("No token received from server");
      }

      // Saves token and remembered email
      localStorage.setItem("token", token);
      if (formData.rememberMe) {
        localStorage.setItem("rememberedEmail", formData.email);
      } else {
        localStorage.removeItem("rememberedEmail");
      }

      setIsAuthenticated(true);
      navigate("/dashboard");
    } catch (error) {
      setError(error.message);
      setLoading(false);
    }
  };

  // Updates form on input change
  const handleChange = (e) => {
    const { name, value, checked, type } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    setFieldErrors(prev => ({ ...prev, [name]: '' }));
    setError(null);
  };

  return (
    <div className="w-full h-screen flex items-start">
      <div className="relative w-1/2 h-full flex flex-col">
        <div className="absolute top-[20%] left-[10%] flex flex-col">
          <h1 className="text-4xl text-white font-bold my-4">
            Customer Database
          </h1>
          <p className="text-xl text-white font-normal">
            Please login to your account
          </p>
        </div>
        <img
          src={COVER_IMAGE}
          className="w-full h-full object-cover"
          alt="Cover"
        />
      </div>

      <div
        className="w-1/2 h-full bg-gray-50"
      >
        <form
          onSubmit={handleLogin}
          className="p-20 flex flex-col justify-between h-full"
        >
          <h1 className="text-xl font-semibold">SwopShop</h1>

          <div className="flex flex-col max-w-[550px] mb-8">
            <h3 className="text-3xl font-semibold mb-2">Login</h3>
            <p className="text-sm mb-2">Please enter your credentials below</p>

            <div className="relative mb-4">
              <input
                type="email"
                name="email"
                placeholder="Email"
                value={formData.email}
                onChange={handleChange}
                className={`w-full py-2 my-2 bg-transparent border-b ${
                  fieldErrors.email ? "border-red-500" : "border-black"
                } outline-none focus:border-blue-500 transition-colors`}
                disabled={loading}
              />
              {fieldErrors.email && (
                <p className="text-red-500 text-xs mt-1">{fieldErrors.email}</p>
              )}
            </div>

            <div className="relative mb-4">
              <input
                type={showPassword ? "text" : "password"}
                name="password"
                placeholder="Password"
                value={formData.password}
                onChange={handleChange}
                className={`w-full py-2 my-2 bg-transparent border-b ${
                  fieldErrors.password ? "border-red-500" : "border-black"
                } outline-none focus:border-blue-500 transition-colors`}
                disabled={loading}
              />
              <button
                type="button"
                className="absolute right-0 top-2 text-xs underline text-gray-600 hover:text-gray-900"
                onClick={() => setShowPassword(!showPassword)}
                disabled={loading}
              >
                {showPassword ? "Hide" : "Show"}
              </button>
              {fieldErrors.password && (
                <p className="text-red-500 text-xs mt-1">{fieldErrors.password}</p>
              )}
            </div>
          </div>

          {error && (
            <div className="bg-red-50 border-l-4 border-red-500 p-4 mb-4">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg
                    className="h-5 w-5 text-red-400"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path
                      fillRule="evenodd"
                      d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                      clipRule="evenodd"
                    />
                  </svg>
                </div>
                <div className="ml-3">
                  <p className="text-sm text-red-700">{error}</p>
                </div>
              </div>
            </div>
          )}

          <div className="flex items-center justify-between mb-8">
            <label className="flex items-center text-sm cursor-pointer">
              <input
                type="checkbox"
                name="rememberMe"
                checked={formData.rememberMe}
                onChange={handleChange}
                className="w-4 h-4 mr-2 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                disabled={loading}
              />
              Remember me for 30 days
            </label>
            <Link
              to="/forgot-password"
              className="text-sm font-medium text-gray-600 hover:text-gray-900 underline underline-offset-2"
            >
              Forgot Password?
            </Link>
          </div>

          <button
            type="submit"
            className={`w-2/3 text-white rounded-md p-4 flex items-center justify-center mb-4 ${
              loading ? "bg-gray-400 cursor-not-allowed" : "bg-gray-900 hover:opacity-90"
            } transition-opacity`}
            disabled={loading}
          >
            {loading ? (
              <span className="flex items-center">
                <svg
                  className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  ></circle>
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  ></path>
                </svg>
                Logging in...
              </span>
            ) : (
              "Login"
            )}
          </button>

          <p className="text-center text-sm text-gray-600">
            Don't have an account? Contact your administrator to get help
          </p>
        </form>
      </div>
    </div>
  );
};

export default LoginPage;